/**
 * Storage Service Type Definitions
 * Following the storage-srv/storage-controller.md specifications
 */

/**
 * Standard API response wrapper for storage operations
 */
export interface StorageAPIResponse<T> {
  success: boolean;
  code: number;
  description?: string;
  data: T;
  timestamp?: string;
}

/**
 * Error response structure for storage operations
 */
export interface StorageErrorResponse {
  success: false;
  code: number;
  error: {
    error_code: string;
    error_description: string;
    meta_data?: Record<string, any>;
  };
  timestamp?: string;
}

/**
 * File upload response data (matches actual API response)
 */
export interface FileUploadResponse {
  name: string; // Original filename
  key: string; // Storage key (UUID-based)
  url: string; // Full URL to access the file
  fileType: string; // MIME type (e.g., "application/pdf")
  
  // Optional fields that may be added later
  file_id?: string;
  file_size?: number;
  upload_timestamp?: string;
  // Additional metadata from StorageService
  [key: string]: any;
}

/**
 * File upload request (for internal use)
 */
export interface FileUploadRequest {
  file: File;
  metadata?: Record<string, any>;
}

/**
 * Upload progress tracking
 */
export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

/**
 * Upload options for file upload
 */
export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  metadata?: Record<string, any>;
}

/**
 * Storage error codes enum
 */
export enum StorageErrorCode {
  MISSING_FILE = 'MISSING_FILE',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  UNSUPPORTED_FILE_TYPE = 'UNSUPPORTED_FILE_TYPE',
  STORAGE_SERVICE_ERROR = 'STORAGE_SERVICE_ERROR',
}

/**
 * Supported file types
 */
export enum SupportedFileTypes {
  PDF = 'pdf',
  DOCX = 'docx',
  DOC = 'doc',
  PPTX = 'pptx',
  TXT = 'txt',
  CSV = 'csv',
  JPG = 'jpg',
  JPEG = 'jpeg',
  PNG = 'png',
  GIF = 'gif',
  WEBP = 'webp',
  MP4 = 'mp4',
  AVI = 'avi',
  MOV = 'mov',
  MP3 = 'mp3',
  WAV = 'wav',
  M4A = 'm4a',
}

/**
 * File type categories
 */
export enum FileCategory {
  DOCUMENT = 'document',
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DATA = 'data',
  UNKNOWN = 'unknown',
}

/**
 * File metadata interface
 */
export interface FileMetadata {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  category: FileCategory;
  extension: string;
}

/**
 * Batch upload result
 */
export interface BatchUploadResult {
  successful: FileUploadResponse[];
  failed: Array<{
    file: File;
    error: string;
  }>;
  total: number;
  successCount: number;
  failureCount: number;
}